'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { USER } from 'self-service-api/constants/UnitTypes';
import { skuSelector } from '../../selectors/ProductsSelectors';
import classNames from '../../utils/classNames';
import AdditionalUnitsPicker from './AdditionalUnitsPicker';
import Divider from './Divider';
import SeatsPicker from '../common/SeatsPicker';
import { PlanBuilderContext } from './PlanBuilder';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H5 from 'UIComponents/elements/headings/H5';
import { MONTHLY } from 'self-service-api/constants/Terms';

const QuantityPacksPickerContainer = ({
  quantityPackSkuIds,
  setProductLevel,
  trackingSource
}) => {
  const {
    productConfigurations,
    setProductConfigurations,
    showDiscountedPricesInUnitsPicker,
    queryParamTerm
  } = useContext(PlanBuilderContext);

  const getSelectedQuantityPackQuantity = quantityPackSkuId => {
    const foundConfig = productConfigurations.find(({
      skuId
    }) => skuId === quantityPackSkuId);
    return foundConfig ? foundConfig.quantity : 0;
  };

  const setQuantityPackQuantity = (quantity, quantityPackSkuId) => {
    setProductConfigurations(() => {
      const filteredConfigs = productConfigurations.filter(({
        skuId
      }) => skuId !== quantityPackSkuId);

      if (quantity > 0) {
        filteredConfigs.push(createProductConfig({
          skuId: quantityPackSkuId,
          quantity
        }));
      }

      return filteredConfigs;
    });
  };

  const nonUserQuantityPackSkuIds = useSelector(state => {
    return quantityPackSkuIds.filter(skuId => skuSelector(state, {
      skuId
    }).quantityUnit.unitType !== USER);
  });
  const usersQuantityPackSkuIds = useSelector(state => {
    return quantityPackSkuIds.filter(skuId => skuSelector(state, {
      skuId
    }).quantityUnit.unitType === USER);
  }); // Need to group the sales / service seats in a way that looks nice

  let pickerComponents = nonUserQuantityPackSkuIds.map(quantityPackSkuId => /*#__PURE__*/_jsxs(React.Fragment, {
    children: [/*#__PURE__*/_jsx(Divider, {
      className: "m-y-7"
    }), /*#__PURE__*/_jsx(AdditionalUnitsPicker, {
      quantityPackSkuId: quantityPackSkuId,
      quantityPackQuantity: getSelectedQuantityPackQuantity(quantityPackSkuId),
      setQuantityPackQuantity: (quantity, skuId = quantityPackSkuId) => setQuantityPackQuantity(quantity, skuId),
      productConfigurations: productConfigurations,
      setActiveProductLevel: setProductLevel,
      queryParamTerm: showDiscountedPricesInUnitsPicker ? queryParamTerm : MONTHLY,
      trackingSource: trackingSource,
      setProductConfigurations: setProductConfigurations
    })]
  }, quantityPackSkuId));
  const hasMultipleUserPickers = usersQuantityPackSkuIds.length > 1;

  if (hasMultipleUserPickers) {
    pickerComponents.push( /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsx(Divider, {
        className: "m-y-7"
      }), /*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.planBuilder.paidUsers"
        })
      })]
    }, 'seatsTitle'));
  }

  pickerComponents = pickerComponents.concat(usersQuantityPackSkuIds.map((quantityPackSkuId, index) => /*#__PURE__*/_jsxs(React.Fragment, {
    children: [!hasMultipleUserPickers && /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(SeatsPicker, {
      quantityPackSkuId: quantityPackSkuId,
      quantityPackQuantity: getSelectedQuantityPackQuantity(quantityPackSkuId),
      onChange: quantity => setQuantityPackQuantity(quantity, quantityPackSkuId),
      productConfigurations: productConfigurations,
      useProductLineSpecificTitle: usersQuantityPackSkuIds.length > 1,
      hideFreeUsersDisclaimer: index < usersQuantityPackSkuIds.length - 1,
      className: classNames(hasMultipleUserPickers && 'm-top-6'),
      allProductConfigurations: productConfigurations,
      queryParamTerm: showDiscountedPricesInUnitsPicker ? queryParamTerm : MONTHLY,
      trackingSource: trackingSource
    })]
  }, quantityPackSkuId)));
  return pickerComponents;
};

export default QuantityPacksPickerContainer;